import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Components & Utils
import Notification from '../components/general/notification/Notification';
import Container from '../components/group/Container';
import NavBar from '../components/general/navBar/NavBar';
import Settings from '../components/doctor/navigation/navBar/Settings'; 
import Sidebar from '../components/general/navigation/Sidebar';
import LoadingPopup from '../components/general/popups/LoadingPopup';
import Page from '../components/general/containers/Page';
import Invite from '../components/group/groupAccess/popups/Invite';
import SuscribedPopup from '../components/general/popups/SuscribedPopup';
import UpdateSeatsPopup from '../components/group/billing/items/UpdateSeatsPopup';
import AddSeatsPopup from '../components/general/popups/AddSeatsPopup';
import CancelSubscriptionPopup from '../components/general/popups/CancelSubscriptionPopup';
import AddPaymentMethodPopup from '../components/group/billing/items/AddPaymentMethodPopup';

// Redux & Routes
import { setSideBarMenu, setDashboardSection, setIsSuscribedPopupOpened, setIsAddSeatsPopupOpened, setIsCancelSubscriptionPopupOpened, setIsUpdateSeatsPopupOpened, setIsAddPaymentMethodPopupOpened } from '../redux/features/general/navigation';
import { getGroupSubscription, verifyCheckoutSession, verifySetupIntentSession } from '../routes/doctor/billing';
import { setCurrentPlan, setGroup, setIsRefreshSubscription, setPaymentMethod, setSubscription, setCustomer, setInvoices, setActiveCount, setNextInvoicePreview } from '../redux/features/doctor/billing/billing';
import { setIsSelectedPlan } from '../redux/features/doctor/billing/paywall';
import { setNotificationMessage } from '../redux/features/general/notification';

// --- START OF FIX: Import the function, not a static object ---
import getGroupMenuItems from '../util/navigation/sidebarMenu/group';
// --- END OF FIX ---

const Group: React.FC = () => {
  const dispatch = useAppDispatch(); 
  const navigate = useNavigate(); 
  const { t } = useTranslation(); 
  const { section: paramFromUrl, setupIntentId } = useParams(); 

  // --- START OF FIX: Get the group data from Redux ---
  const group = useAppSelector(state => state.billing.group);
  // --- END OF FIX ---
  
  const accountInfo = useAppSelector(state => state.meta.accountInfo); 
  const isLoadingPopupOpened = useAppSelector(state => state.navigation.isLoadingPopupOpened);
  const isSuscribedPopupOpened = useAppSelector(state => state.navigation.isSuscribedPopupOpened);
  const isRefreshSubscription = useAppSelector(state => state.billing.isRefreshSubscription); 
  const isUpdateSeatsPopupOpened = useAppSelector(state => state.navigation.isUpdateSeatsPopupOpened); 
  const isAddSeatsPopupOpened = useAppSelector(state => state.navigation.isAddSeatsPopupOpened); 
  const isCancelSubscriptionPopupOpened = useAppSelector(state => state.navigation.isCancelSubscriptionPopupOpened); 
  const isAddPaymentMethodPopupOpened = useAppSelector(state => state.navigation.isAddPaymentMethodPopupOpened); 
  const isGroupAccessInvitePopupOpened = useAppSelector(state => state.navigation.isGroupAccessInvitePopupOpened);

  // --- START OF FIX: Generate the menu dynamically ---
  const menuItems = getGroupMenuItems(group, t);
  // --- END OF FIX ---

  const isKnownSection = menuItems.some(item => item.section === paramFromUrl);
  const currentSectionFromUrl = isKnownSection ? paramFromUrl : undefined;
  const sessionId = !isKnownSection && paramFromUrl ? paramFromUrl : undefined;

  const handleSubscriptionInfo = async () => { 
    const res = await getGroupSubscription(); 
    if (res?.data.status === 200) { 
      dispatch(setCurrentPlan(res.data.currentPlan)); 
      dispatch(setSubscription(res.data.subscription)); 
      dispatch(setGroup(res.data.group)); 
      dispatch(setPaymentMethod(res.data.paymentMethod));
      dispatch(setCustomer(res.data.customer)); 
      dispatch(setInvoices(res.data.invoices)); 
      dispatch(setActiveCount(res.data.activeCount)); 
      dispatch(setNextInvoicePreview(res.data.nextInvoicePreview)); 
    }; 
  }; 

  const handleSessionVerification = async (checkoutSessionId: string) => { 
    const res = await verifyCheckoutSession(checkoutSessionId) as any; 
    if (res.data.status === 200 && res.data.subscription) { 
      if (['trialing', 'active'].includes(res.data.subscription.status)) { 
        dispatch(setIsSuscribedPopupOpened(true)); 
        dispatch(setIsRefreshSubscription(true)); 
        navigate('/group'); 
      }; 
    }; 
  }; 

  const handleSetupIntentVerification = async () => { 
    const res = await verifySetupIntentSession(setupIntentId); 
    if (res?.data.status === 200) { 
      dispatch(setIsRefreshSubscription(true)); 
      dispatch(setNotificationMessage(t('paymentMethodUpdated'))); 
    } else { 
      navigate('/group'); 
      window.location.reload(); 
    }; 
  }; 

  // --- START OF FIX: Update the menu whenever the group data or language changes ---
  useEffect(() => { 
    dispatch(setSideBarMenu(menuItems)); 
  }, [t, dispatch, group]); // Added group to dependency array
  // --- END OF FIX ---

  useEffect(() => {
    if (currentSectionFromUrl) {
        dispatch(setDashboardSection(currentSectionFromUrl));
    } else if (!sessionId && !setupIntentId && menuItems.length > 0) {
        navigate(`/group/${menuItems[0].section}`, { replace: true });
    }
  }, [currentSectionFromUrl, sessionId, setupIntentId, dispatch, navigate, menuItems]);

  useEffect(() => { 
    if (accountInfo) { 
      handleSubscriptionInfo(); 
    }; 
  },[accountInfo]);  

  useEffect(() => { 
    if (isRefreshSubscription) {
      handleSubscriptionInfo(); 
      dispatch(setIsRefreshSubscription(false)); 
    }; 
  },[isRefreshSubscription]); 

  useEffect(() => { 
    if (sessionId) { 
      handleSessionVerification(sessionId); 
      dispatch(setIsSelectedPlan(false)); 
    }; 
  },[sessionId]); 

  useEffect(() => { 
    if (setupIntentId) { 
      handleSetupIntentVerification(); 
    }; 
  },[setupIntentId]); 

  return (
        <Page>
          <NavBar>
              <Settings /> 
          </NavBar>
          <Sidebar />
          <Container/>
          {isUpdateSeatsPopupOpened && <UpdateSeatsPopup />}
          {isAddSeatsPopupOpened && <AddSeatsPopup />}
          {isSuscribedPopupOpened && <SuscribedPopup />}
          {isCancelSubscriptionPopupOpened && <CancelSubscriptionPopup />}
          {isAddPaymentMethodPopupOpened && <AddPaymentMethodPopup />}
          {isGroupAccessInvitePopupOpened && <Invite />}
          {isLoadingPopupOpened && <LoadingPopup />}
        </Page>
  );
};

export default Group;